import clsx from 'clsx'
import { FC, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { CompareIcon } from '@svgs'
import { toggleCompare } from 'src/redux/slices/propertySearchSlice'
import { RootState } from 'src/redux/store'

interface ICompareButton {
  propertyId: string
  // Larger round control for card overlays; the announcement page passes its
  // own sizing via className.
  className?: string
}

// The "⇄" compare toggle, reusable on map cards, the announcement page and the
// wishlist (owner feedback B6: compare must be available everywhere, not only
// in favourites). Three states: in the set, invited (something else is already
// picked), and idle.
const CompareButton: FC<ICompareButton> = ({ propertyId, className }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const compareIds = useSelector((state: RootState) => state.propertySearch.compareIds)
  const isCompared = compareIds.includes(propertyId)
  const othersPicked = compareIds.length > 0 && !isCompared

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    // Card overlays sit inside a <Link>; keep the toggle from navigating.
    e.preventDefault()
    e.stopPropagation()
    dispatch(toggleCompare(propertyId))
  }

  return (
    <button
      onClick={handleClick}
      title={t('compare.add')}
      aria-label={t('compare.add')}
      aria-pressed={isCompared}
      className={clsx(
        'flex items-center justify-center w-11 h-11 md:w-9 md:h-9 rounded-full shadow-sm pointer-events-auto text-base font-black transition-colors duration-200',
        isCompared
          ? 'bg-brand-strong text-white'
          : othersPicked
            ? 'bg-surface text-brand ring-2 ring-brand'
            : 'bg-surface text-brand hover:bg-brand-soft',
        className,
      )}
    >
      <CompareIcon className="w-5 h-5 md:w-4 md:h-4" />
    </button>
  )
}

export default CompareButton
